"use client";

import React from "react";
import { BarChart } from "@mui/x-charts/BarChart";
import { Paper } from "@mui/material";
import { Typography } from "./wrappers/Typography";
import { formatPercent } from "@/utils/valueFormatters";
import { chartColors } from "@/theme/chartColors";
import { useTranslation } from "react-i18next";

type DemographicData = {
    gender: {
        male: number;
        female: number;
    };
    generation: {
        babyBoomer: number;
        genX: number;
        genY: number;
        genZ: number;
    };
};

type DemographicComparisonProps = {
    data: {
        your_dealership: DemographicData;
        your_peers: DemographicData;
        all_dealerships: DemographicData;
    };
    isGroup?: boolean;
};

const DemographicComparisonChart: React.FC<DemographicComparisonProps> = ({
    data,
    isGroup = false,
}) => {
    const { t } = useTranslation();

    const labels = [
        ...Object.keys(data.your_dealership?.gender),
        ...Object.keys(data.your_dealership.generation),
    ].map((key, i) =>
        t(i === 0 || i === 1 ? `demographics.gender.${key}` : `demographics.generations.${key}`)
    );

    const yourDealershipData = [
        ...Object.values(data.your_dealership?.gender),
        ...Object.values(data.your_dealership.generation),
    ];
    const yourPeersData = [
        ...Object.values(data.your_peers?.gender),
        ...Object.values(data.your_peers.generation),
    ];
    const allDealershipsData = [
        ...Object.values(data.all_dealerships?.gender),
        ...Object.values(data.all_dealerships.generation),
    ];

    const hasAnyValue = (values: (number | null | undefined)[]) =>
        values.some((v) => v != null && v !== 0);

    const peersHaveCompositionData = hasAnyValue(yourPeersData);

    const allSeries = [
        {
            label: isGroup
                ? t("common.comparisonGroups.yourGroup")
                : t("common.comparisonGroups.yourDealership"),
            data: yourDealershipData,
        },
        {
            label: t("common.comparisonGroups.yourPeers"),
            data: yourPeersData,
        },
        {
            label: t("common.comparisonGroups.allDealerships"),
            data: allDealershipsData,
        },
    ];

    const chartData = {
        labels: labels,
        series: peersHaveCompositionData
            ? allSeries
            : allSeries.filter((s, idx) => idx !== 1),
    };

    return (
        <Paper
            className="exec-step-8"
            elevation={0}
            sx={{ p: 2, borderRadius: "16px", border: "1px solid #dee2e6" }}
        >
            <Typography
                variant="h6"
                component="h3"
                tx="executiveSummary.sections.demographicComparison"
                sx={{ mb: 2, fontWeight: "bold", color: "#002046" }}
            />
            {!peersHaveCompositionData && (
                <Typography
                    variant="body2"
                    tx="executiveSummary.notes.demographicComparisonNoPeerData"
                    sx={{ mb: 2, color: "text.secondary", fontStyle: "italic" }}
                />
            )}
            <BarChart
                height={400}
                xAxis={[
                    {
                        scaleType: "band",
                        data: chartData.labels,
                        valueFormatter: (value: string, context: { location: string }) => {
                            if (context.location === "tooltip") {
                                return value;
                            } else {
                                return value.replace(/\s*\([^)]*\)/g, "");
                            }
                        },
                    },
                ]}
                yAxis={[
                    {
                        valueFormatter: (value: number | null) =>
                            formatPercent((value ?? 0) * 100, 0),
                        max: 1,
                    },
                ]}
                series={chartData.series.map((s) => ({
                    data: s.data,
                    label: s.label,
                    valueFormatter: (value: number | null) => formatPercent((value ?? 0) * 100, 1),
                }))}
                colors={
                    peersHaveCompositionData
                        ? chartColors
                        : [chartColors[0], chartColors[2]]
                }
                slotProps={{
                    legend: {
                        position: { vertical: "top", horizontal: "center" },
                    },
                }}
            />
        </Paper>
    );
};

export default DemographicComparisonChart;
